<script setup lang="ts">
import { SwiperSlide } from 'swiper/vue';
import { computed } from 'vue';

import { useGlobalStyle } from '../../store/templateData';
import ModuleCarouselSwiper from '../ModuleCarouselSwiper/ModuleCarouselSwiper.vue';
import Custom from './components/Custom/Custom.vue';
import Example from './components/Example/Example.vue';

defineOptions({
  name: 'ModuleCarouselUserCustomDisplay'
});

interface Props {
  module: {
    components: {
      title: {
        text?: string;
      };
      image1: {
        src?: string;
      };
      image2: {
        src?: string;
      };
      image3: {
        src?: string;
      };
      image4: {
        src?: string;
      };
      image5: {
        src?: string;
      };
      image6: {
        src?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: { text: '' },
      image1: { src: '' },
      image2: { src: '' },
      image3: { src: '' },
      image4: { src: '' },
      image5: { src: '' },
      image6: { src: '' }
    }
  })
});

const components = computed(() => props.module.components);

const imageArray = computed(() => {
  return [
    {
      id: 1,
      src: components.value.image1.src,
    },
    {
      id: 2,
      src: components.value.image2.src,
    },
    {
      id: 3,
      src: components.value.image3.src,
    },
    {
      id: 4,
      src: components.value.image4.src,
    },
    {
      id: 5,
      src: components.value.image5.src,
    },
    {
      id: 6,
      src: components.value.image6.src,
    },
  ].filter((image) => image.src !== '');
});

const isAllImageEmpty = computed(() => imageArray.value.length === 0);
const needRender = computed(() => !isAllImageEmpty.value);

const { titleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-carousel-user-custom-display" v-if="needRender">
  <div
    class="module-carousel-user-custom-display__title"
    v-if="components.title.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <ModuleCarouselSwiper>
    <template #default="{ swiperRef }">
      <template v-if="isAllImageEmpty">
        <SwiperSlide v-for="index in 6" :key="index" :swiperRef="swiperRef">
          <Example/>
        </SwiperSlide>
      </template>
      <template v-else>
        <SwiperSlide
          v-for="image in imageArray"
          :key="image.id"
          :swiperRef="swiperRef"
          :backgroundImage="image.src"
        >
          <Custom :backgroundImage="image.src ?? ''"/>
        </SwiperSlide>
      </template>
    </template>
  </ModuleCarouselSwiper>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-carousel-user-custom-display {
  @apply py-4;
  @apply mx-auto;

  &__title {
    @apply mb-4 font-medium break-all;
    @apply text-4xl text-center whitespace-pre-line;
    // mobile版圖片滿版，但文字要與其他 module 等寬 (留 20px 的 padding)
    @apply px-5;

    word-wrap: break-word;
  }
}

// tablet
.tablet .module-carousel-user-custom-display {
  @apply px-10;

  &__title {
    @apply px-0;
  }
}

// PC - 1024px
.laptop .module-carousel-user-custom-display {
  padding-left: 10%;
  padding-right: 10%;

  @apply py-8;

  &__title {
    @apply px-0;
  }
}

// PC -1440px
.desktop .module-carousel-user-custom-display {
  width: $desktop-fixed-width;

  @apply py-8;

  &__title {
    @apply px-0;
  }
}
</style>
